import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Check, Delete, Undo } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';

import {
  AdvertRanking,
  AdvertsGetResponse,
  getAdminAdverts,
  getEntityRankings,
  updateEntityRankings,
} from '~/services/MerchandiseService';

import AddRankingDialog, { UpsertRankingData } from './AddRankingDialog';

const DEFAULT_COLUMN_PROPS: Partial<GridColDef> = {
  sortable: false,
  disableColumnMenu: true,
  flex: 1,
  display: 'flex',
};

const INPUT_BASE_STYLE = {
  width: '50px',
  height: '28px',
  textAlign: 'center' as const,
  borderRadius: '4px',
  fontSize: '0.875rem',
  padding: '4px',
};

const SNACKBAR_OPTIONS = {
  anchorOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
};

const ADVERT_BATCH_SIZE = 100;

// Extended advert type that includes admin-only fields returned with adminView=true
type FullAdvert = AdvertsGetResponse['result']['adverts'][number] & {
  isOnline?: boolean;
  isDeleted?: boolean;
  salesCount?: number;
};

// Helper to batch an array into chunks
const batchArray = <T,>(arr: T[], size: number): T[][] => {
  const batches: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    batches.push(arr.slice(i, i + size));
  }
  return batches;
};

// Helper to merge rankings with advert details
const mergeRankingsWithAdverts = (
  rankings: { entityKey: string; rank: number; createdAt: string; updatedAt: string }[],
  advertsMap: Map<string, FullAdvert>,
): AdvertRanking[] => {
  return rankings.map((ranking) => {
    const advert = advertsMap.get(ranking.entityKey);
    return {
      rank: ranking.rank,
      advertId: ranking.entityKey,
      title: advert?.name ?? null,
      slug: advert?.slug ?? null,
      brandName: advert?.brand?.name ?? null,
      categoryName: advert?.category?.displayName ?? null,
      isOnline: advert?.isOnline ?? null,
      salesCount: advert?.salesCount ?? null,
      isDeleted: advert?.isDeleted ?? null,
      createdAt: ranking.createdAt,
      updatedAt: ranking.updatedAt,
    };
  });
};

// Helper to recalculate ranks to be continuous (1, 2, 3, ...)
const recalculateRanks = (items: AdvertRanking[]): AdvertRanking[] => {
  return items.map((row, index) => ({
    ...row,
    rank: index + 1,
  }));
};

// Clamp rank to valid range: 1 to maxLength+1 (for inserts) or maxLength (for moves)
const getValidRank = (rank: number, maxLength: number, isInsert = false): number => {
  const max = isInsert ? maxLength + 1 : maxLength;
  if (rank <= 0) return 1;
  if (rank > max) return max;
  return rank;
};

export default function Ranking() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);

  // Step 1: Fetch entity rankings
  const {
    data: entityRankings,
    isLoading: isLoadingRankings,
    error: rankingsError,
  } = useQuery({
    queryKey: ['merchandise', 'entity-rankings', 'ADVERT'],
    queryFn: async ({ signal }) => {
      const response = await getEntityRankings('ADVERT', { signal });
      return response.result.entityRankings;
    },
  });

  // Extract advertIds from rankings for the second query
  const advertIds = useMemo(() => {
    return entityRankings?.map((r) => r.entityKey) ?? [];
  }, [entityRankings]);

  // Step 2: Fetch advert details (dependent on rankings, batched by 100)
  const {
    data: adverts,
    isLoading: isLoadingAdverts,
    error: advertsError,
  } = useQuery({
    queryKey: ['merchandise', 'adverts', advertIds],
    queryFn: async ({ signal }) => {
      if (advertIds.length === 0) return [] as FullAdvert[];

      const batches = batchArray<string>(advertIds, ADVERT_BATCH_SIZE);
      const results = await Promise.all(batches.map((batchIds: string[]) => getAdminAdverts(batchIds, { signal })));

      return results.flatMap((r) => r.result.adverts) as FullAdvert[];
    },
    enabled: !!entityRankings && entityRankings.length > 0,
  });

  // Combine rankings with advert details
  const fetchedData = useMemo(() => {
    if (!entityRankings || !adverts) return undefined;

    const advertsMap = new Map<string, FullAdvert>();
    adverts.forEach((advert) => advertsMap.set(advert.id, advert));

    return mergeRankingsWithAdverts(entityRankings, advertsMap);
  }, [entityRankings, adverts]);

  const isLoading = isLoadingRankings || isLoadingAdverts;
  const error = rankingsError || advertsError;

  // Local state for managing rankings with drag and drop
  const [rankings, setRankings] = useState<AdvertRanking[]>([]);
  // Deleted items - kept separately so they can be restored
  const [deletedRankings, setDeletedRankings] = useState<AdvertRanking[]>([]);

  // Track temporary input values before commit - using ref to avoid re-renders on every keystroke
  const tempRankInputsRef = useRef<Record<string, string>>({});
  const [changedInputs, setChangedInputs] = useState<Set<string>>(new Set());

  // Combined display rows: active rankings + deleted items at the end (greyed out)
  const displayRows = useMemo(() => {
    return [...rankings, ...deletedRankings];
  }, [rankings, deletedRankings]);

  // Set of deleted advert IDs for quick lookup
  const deletedIds = useMemo(() => new Set(deletedRankings.map((r) => r.advertId)), [deletedRankings]);

  // Compute diff between original fetchedData and current rankings
  const changeSummary = useMemo(() => {
    if (!fetchedData) return { added: [], deleted: [], moved: [], hasChanges: false };

    const originalIds = new Set(fetchedData.map((r) => r.advertId));
    const originalRankMap = new Map(fetchedData.map((r) => [r.advertId, r.rank]));

    const added = rankings.filter((r) => !originalIds.has(r.advertId));
    // Deleted = items in deletedRankings that were originally in fetchedData
    const deleted = deletedRankings.filter((r) => originalIds.has(r.advertId));
    const moved = rankings.filter((r) => {
      const originalRank = originalRankMap.get(r.advertId);
      return originalRank !== undefined && originalRank !== r.rank;
    });

    const hasChanges = added.length > 0 || deleted.length > 0 || moved.length > 0;

    return { added, deleted, moved, hasChanges };
  }, [fetchedData, rankings, deletedRankings]);

  // Set of newly added advert IDs (for displaying "Added Just Now")
  const newlyAddedIds = useMemo(() => {
    return new Set(changeSummary.added.map((r) => r.advertId));
  }, [changeSummary.added]);

  // Mutation for submitting rankings
  const submitMutation = useMutation({
    mutationFn: (entityKeys: string[]) => updateEntityRankings(entityKeys),
    onSuccess: () => {
      enqueueSnackbar('Rankings updated successfully', { variant: 'success', ...SNACKBAR_OPTIONS });
      setIsSubmitDialogOpen(false);
      // Invalidate both queries
      queryClient.invalidateQueries({ queryKey: ['merchandise', 'entity-rankings', 'ADVERT'] });
      queryClient.invalidateQueries({ queryKey: ['merchandise', 'adverts'] });
    },
    onError: (err) => {
      enqueueSnackbar(`Failed to update rankings: ${err instanceof Error ? err.message : 'Unknown error'}`, {
        variant: 'error',
        ...SNACKBAR_OPTIONS,
      });
    },
  });

  // Sync local state with fetched data (ensure sorted by rank)
  useEffect(() => {
    if (fetchedData) {
      const sortedData = [...fetchedData].sort((a, b) => a.rank - b.rank);
      setRankings(sortedData);
      setDeletedRankings([]); // Reset deleted items when fresh data is fetched
    }
  }, [fetchedData]);

  const handleRowOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      // Prevent reordering of deleted items
      if (deletedIds.has(params.row.advertId)) return;

      const oldIndex = rankings.findIndex((row) => row.advertId === params.row.advertId);
      if (oldIndex === -1) return;

      // Clamp target index to valid range (within active rankings only)
      const newIndex = Math.max(0, Math.min(params.targetIndex, rankings.length - 1));

      // Create a new array with the row moved to the new position
      const newRankings = [...rankings];
      const [movedRow] = newRankings.splice(oldIndex, 1);
      newRankings.splice(newIndex, 0, movedRow);

      setRankings(recalculateRanks(newRankings));
    },
    [rankings, deletedIds],
  );

  const handleRankEdit = useCallback(
    (newRank: number, advertId: string) => {
      const oldIndex = rankings.findIndex((row) => row.advertId === advertId);
      if (oldIndex === -1) return;

      // Clamp rank to valid range (for moves, max is rankings.length)
      const validRank = getValidRank(newRank, rankings.length, false);
      const newIndex = validRank - 1; // Convert rank (1-based) to index (0-based)

      const newRankings = [...rankings];
      const [movedRow] = newRankings.splice(oldIndex, 1);
      newRankings.splice(newIndex, 0, movedRow);

      setRankings(recalculateRanks(newRankings));
    },
    [rankings],
  );

  const handleUpsert = useCallback(
    (data: UpsertRankingData) => {
      const { advertId, title, brandName, categoryName, rank, isNew } = data;

      // Check if item is in deleted list first
      const deletedItem = deletedRankings.find((r) => r.advertId === advertId);
      if (deletedItem) {
        // Restore from deleted and place at specified rank
        setDeletedRankings((prev) => prev.filter((r) => r.advertId !== advertId));

        const validRank = getValidRank(rank, rankings.length, true);
        const newRankings = [...rankings];
        newRankings.splice(validRank - 1, 0, deletedItem);

        setRankings(recalculateRanks(newRankings));
        return;
      }

      if (isNew) {
        // Add new ranking
        const newRanking: AdvertRanking = {
          advertId,
          title,
          brandName,
          categoryName,
          slug: data.slug,
          isOnline: data.isOnline,
          salesCount: data.salesCount,
          rank,
          createdAt: null,
          updatedAt: null,
          isDeleted: false,
        };

        const validRank = getValidRank(rank, rankings.length, true);
        const newRankings = [...rankings];
        newRankings.splice(validRank - 1, 0, newRanking);

        setRankings(recalculateRanks(newRankings));
      } else {
        // Edit existing ranking - move to new position
        const oldIndex = rankings.findIndex((row) => row.advertId === advertId);
        if (oldIndex === -1) return;

        const validRank = getValidRank(rank, rankings.length, false);
        const newIndex = validRank - 1;
        const newRankings = [...rankings];
        const [movedRow] = newRankings.splice(oldIndex, 1);
        newRankings.splice(newIndex, 0, movedRow);

        setRankings(recalculateRanks(newRankings));
      }
    },
    [rankings, deletedRankings],
  );

  const handleDelete = useCallback(
    (advertId: string) => {
      const item = rankings.find((r) => r.advertId === advertId);
      if (!item) return;

      // Clean up any pending input state
      delete tempRankInputsRef.current[advertId];
      setChangedInputs((prev) => {
        const next = new Set(prev);
        next.delete(advertId);
        return next;
      });

      // Move item to deleted list (keeping its rank for potential restore)
      setDeletedRankings((prev) => [...prev, item]);

      // Remove from active rankings and recalculate ranks
      const newRankings = rankings.filter((r) => r.advertId !== advertId);
      setRankings(recalculateRanks(newRankings));
    },
    [rankings],
  );

  const handleRestore = useCallback(
    (advertId: string) => {
      const item = deletedRankings.find((r) => r.advertId === advertId);
      if (!item) return;

      // Remove from deleted list
      setDeletedRankings((prev) => prev.filter((r) => r.advertId !== advertId));

      // Insert at the item's saved rank (clamped to valid range)
      const validRank = getValidRank(item.rank, rankings.length, true);
      const newRankings = [...rankings];
      newRankings.splice(validRank - 1, 0, item);

      setRankings(recalculateRanks(newRankings));
    },
    [rankings, deletedRankings],
  );

  const handleSubmitChanges = () => {
    const entityKeys = rankings.map((r) => r.advertId);
    submitMutation.mutate(entityKeys);
  };

  const handleResetChanges = useCallback(() => {
    if (fetchedData) {
      const sortedData = [...fetchedData].sort((a, b) => a.rank - b.rank);
      setRankings(sortedData);
      setDeletedRankings([]);
      setChangedInputs(new Set());
      tempRankInputsRef.current = {};
    }
  }, [fetchedData]);

  const columns: GridColDef<AdvertRanking>[] = useMemo(
    () => [
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'rank',
        headerName: 'Rank',
        flex: 0.5,
        renderCell: (params) => {
          const advertId = params.row.advertId;
          const isDeleted = deletedIds.has(advertId);

          // For deleted items, show greyed rank (not editable)
          if (isDeleted) {
            return (
              <Typography variant="body2" color="text.disabled">
                ({params.value})
              </Typography>
            );
          }

          const hasChanged = changedInputs.has(advertId);
          const defaultValue = params.value.toString();

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <input
                type="text"
                defaultValue={defaultValue}
                key={`${advertId}-${params.value}`}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow digits (no dots, no other characters)
                  if (value === '' || /^\d+$/.test(value)) {
                    tempRankInputsRef.current[advertId] = value;
                    // Only update state to show visual changes if needed
                    if (value !== defaultValue && !hasChanged) {
                      setChangedInputs((prev) => new Set(prev).add(advertId));
                    } else if (value === defaultValue && hasChanged) {
                      setChangedInputs((prev) => {
                        const next = new Set(prev);
                        next.delete(advertId);
                        return next;
                      });
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const currentValue = tempRankInputsRef.current[advertId] || defaultValue;
                    const newRank = Number(currentValue);
                    if (!isNaN(newRank)) {
                      handleRankEdit(newRank, advertId);
                      delete tempRankInputsRef.current[advertId];
                      setChangedInputs((prev) => {
                        const next = new Set(prev);
                        next.delete(advertId);
                        return next;
                      });
                    }
                  }
                }}
                style={{
                  ...INPUT_BASE_STYLE,
                  border: hasChanged ? '2px solid #1976d2' : '1px solid #ccc',
                  backgroundColor: hasChanged ? '#e3f2fd' : 'white',
                }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  const currentValue = tempRankInputsRef.current[advertId] || defaultValue;
                  const newRank = Number(currentValue);
                  if (!isNaN(newRank)) {
                    handleRankEdit(newRank, advertId);
                    delete tempRankInputsRef.current[advertId];
                    setChangedInputs((prev) => {
                      const next = new Set(prev);
                      next.delete(advertId);
                      return next;
                    });
                  }
                }}
                disabled={!hasChanged}
                sx={{
                  padding: '6px',
                  backgroundColor: hasChanged ? '#2e7d32' : 'transparent',
                  color: hasChanged ? 'white' : 'action.disabled',
                  '&:hover': {
                    backgroundColor: hasChanged ? '#1b5e20' : 'transparent',
                  },
                  '&:disabled': {
                    backgroundColor: 'transparent',
                    opacity: 0.4,
                  },
                }}
              >
                <Check sx={{ fontSize: '18px' }} />
              </IconButton>
            </Box>
          );
        },
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'title',
        headerName: 'Title',
        flex: 2,
        renderCell: (params) => {
          const { title, slug, advertId, isOnline } = params.row;
          if (!title) {
            return <Typography color="text.disabled">N/A</Typography>;
          }
          // Only make clickable if online and has slug
          if (isOnline && slug) {
            const url = `https://luxuryescapes.com/au/shop/offer/${slug}/${advertId}`;
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                {title}
              </a>
            );
          }
          return <Typography>{title}</Typography>;
        },
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'isOnline',
        headerName: 'Online',
        flex: 0.5,
        renderCell: (params) => {
          if (params.value === true) {
            return (
              <Typography color="success.main" variant="body2">
                Yes
              </Typography>
            );
          }
          if (params.value === false) {
            return (
              <Typography color="error.main" variant="body2">
                No
              </Typography>
            );
          }
          return (
            <Typography color="text.disabled" variant="body2">
              N/A
            </Typography>
          );
        },
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'brandName',
        headerName: 'Brand Name',
        flex: 1.5,
        valueGetter: (value) => value || 'N/A',
        disableColumnMenu: false,
        filterable: true,
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'categoryName',
        headerName: 'Category',
        flex: 1.5,
        valueGetter: (value) => value || 'N/A',
        disableColumnMenu: false,
        filterable: true,
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'salesCount',
        headerName: 'Sales',
        flex: 0.7,
        renderCell: (params) => {
          if (params.value === null || params.value === undefined) {
            return (
              <Typography color="text.disabled" variant="body2">
                N/A
              </Typography>
            );
          }
          return <Typography variant="body2">{params.value.toLocaleString()}</Typography>;
        },
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'createdAt',
        headerName: 'Time Created',
        flex: 1.5,
        renderCell: (params) => {
          if (!params.value) {
            // Only show "Added Just Now" for items newly added in this session
            if (newlyAddedIds.has(params.row.advertId)) {
              return (
                <Typography color="error" variant="body2">
                  Added Just Now
                </Typography>
              );
            }
            return (
              <Typography color="text.disabled" variant="body2">
                N/A
              </Typography>
            );
          }
          return new Date(params.value).toLocaleString();
        },
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'updatedAt',
        headerName: 'Time Updated',
        flex: 1.5,
        renderCell: (params) => {
          if (!params.value) {
            if (newlyAddedIds.has(params.row.advertId)) {
              return (
                <Typography color="error" variant="body2">
                  Added Just Now
                </Typography>
              );
            }
            return (
              <Typography color="text.disabled" variant="body2">
                N/A
              </Typography>
            );
          }
          return new Date(params.value).toLocaleString();
        },
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'advertId',
        headerName: 'Advert ID',
        flex: 1.5,
      },
      {
        ...DEFAULT_COLUMN_PROPS,
        field: 'actions',
        headerName: '',
        flex: 0.5,
        renderCell: (params) => {
          const isDeleted = deletedIds.has(params.row.advertId);

          if (isDeleted) {
            return (
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleRestore(params.row.advertId)}
                title="Restore ranking"
              >
                <Undo />
              </IconButton>
            );
          }

          return (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.advertId)}
              title="Delete ranking"
            >
              <Delete />
            </IconButton>
          );
        },
      },
    ],
    [rankings.length, changedInputs, handleRankEdit, handleDelete, handleRestore, deletedIds, newlyAddedIds],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          Error loading advert rankings: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  if (!rankings || rankings.length === 0) {
    return (
      <Box mt={4}>
        <Typography variant="h6" color="text.secondary">
          No advert rankings found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AddRankingDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        maxRank={rankings.length}
        existingRankings={rankings}
        deletedRankings={deletedRankings}
        onUpsert={handleUpsert}
      />

      {/* Submit Confirmation Dialog */}
      <Dialog open={isSubmitDialogOpen} onClose={() => setIsSubmitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Review your changes before submitting:
          </Typography>

          {changeSummary.added.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="success.main">
                Added ({changeSummary.added.length}):
              </Typography>
              <List dense disablePadding>
                {changeSummary.added.slice(0, 5).map((item) => (
                  <ListItem key={item.advertId} disablePadding sx={{ pl: 2 }}>
                    <ListItemText
                      primary={`"${item.title || 'Unknown'}" at rank #${item.rank}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
                {changeSummary.added.length > 5 && (
                  <ListItem disablePadding sx={{ pl: 2 }}>
                    <ListItemText
                      primary={`...and ${changeSummary.added.length - 5} more`}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {changeSummary.deleted.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="error.main">
                Deleted ({changeSummary.deleted.length}):
              </Typography>
              <List dense disablePadding>
                {changeSummary.deleted.slice(0, 5).map((item) => (
                  <ListItem key={item.advertId} disablePadding sx={{ pl: 2 }}>
                    <ListItemText
                      primary={`"${item.title || 'Unknown'}"`}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
                {changeSummary.deleted.length > 5 && (
                  <ListItem disablePadding sx={{ pl: 2 }}>
                    <ListItemText
                      primary={`...and ${changeSummary.deleted.length - 5} more`}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {changeSummary.moved.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="info.main">
                {changeSummary.moved.length} item(s) changed position
              </Typography>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary">
              This will update rankings for {rankings.length} total items.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSubmitDialogOpen(false)} variant="text">
            Cancel
          </Button>
          <Button onClick={handleSubmitChanges} variant="contained" color="primary" disabled={submitMutation.isPending}>
            {submitMutation.isPending ? 'Submitting...' : 'Submit Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h2">
          Advert Ranking Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button variant="contained" color="primary" onClick={() => setIsAddDialogOpen(true)}>
            Add/Edit ranking
          </Button>
          {changeSummary.hasChanges && (
            <Button variant="text" onClick={handleResetChanges}>
              Reset
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            onClick={() => setIsSubmitDialogOpen(true)}
            disabled={!changeSummary.hasChanges}
          >
            Submit Changes
          </Button>
        </Box>
      </Box>

      <Box mt={2} sx={{ height: 'calc(100vh - 400px)' }}>
        <DataGridPro
          columns={columns}
          rows={displayRows}
          loading={isLoading}
          getRowId={(row: AdvertRanking) => row.advertId}
          disableRowSelectionOnClick
          disableMultipleRowSelection
          rowReordering
          onRowOrderChange={handleRowOrderChange}
          rowHeight={52}
          sx={{
            '& .row-deleted-or-orphaned': {
              color: 'text.disabled',
              backgroundColor: 'action.hover',
              opacity: 0.6,
            },
            '& .row-pending-delete': {
              color: 'text.disabled',
              backgroundColor: 'error.lighter',
              opacity: 0.7,
              textDecoration: 'line-through',
            },
          }}
          getRowClassName={(params) => {
            if (deletedIds.has(params.row.advertId)) {
              return 'row-pending-delete';
            }
            if (params.row.isDeleted === true || params.row.title === null) {
              return 'row-deleted-or-orphaned';
            }
            return '';
          }}
        />
      </Box>
    </>
  );
}
